import {Elements} from '@stripe/react-stripe-js';
import {PaymentRequest, Stripe, StripeElementsOptions} from '@stripe/stripe-js';
import React, {useEffect, useRef, useState} from 'react';
import {Helmet} from 'react-helmet';
import {useHistory} from 'react-router-dom';
import {BottomSheet} from '../../../components/BottomSheet';
import MaybeStagingBadge from '../../../components/MaybeStagingBadge';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import usePaywallDismissTracking from '../../../hooks/usePaywallDismissTracking';
import usePaywallAnalytics from '../../../hooks/usePaywallAnalytics';
import useRedeemedPersonId from '../../../hooks/useRedeemedPersonId';
import {PaymentType, PaywallSlug} from '../../../analytics/paywallEvents';
import {trackPaymentMethodShown} from '../../../analytics/trackPaymentMethodShown';
import PaymentIntentScreen from '../../../screens/StripeCustomCheckout/PaymentIntentScreen';
import Analytics from '../../../utils/Analytics';
import Environment from '../../../utils/Environment';
import {useQueryToken, useValueParams} from '../../../utils/dynamicCodes/hooks';
import {reportRepeatCheckout} from '../../../utils/dynamicCodes/redirects';
import TrialDetailsList from '../components/TrialDetailsList';
import TrialSelectionCells from '../components/TrialSelectionCells';
import {createCheckoutDetails} from '../setupStripeForEmailConfirmationPaywall';
import './EmailConfirmation.css';
import {getEmailConfirmationProducts, ProductCode} from '../products';
import {getSecondChanceOffer} from '../../../second-chance-offer/secondChanceOfferPrices';
import {useDismissTreatment, useDismissTreatmentConfig} from '../../../second-chance-offer/useDismissTreatment';
import DismissCloseButton from '../../../second-chance-offer/DismissCloseButton';
import {SupportedCountryCode} from '../../../utils/currency';
import {useTranslation} from 'react-i18next';
import validateEmail from '../../../utils/validateEmail';
import * as Sentry from '@sentry/react';
import {growthBook} from '../../../index';
import {tokenParam} from '../../../utils/linkOutSession';
import {checkWallet, getWalletTiming, loadWalletStripe} from '../../../utils/walletPrefetch';
import {buttonWaitTiming, nowMs} from '../../../analytics/buttonWaitTiming';

export const stripeStyle = {
  colorPrimary: '#0570de',
  colorBackground: '#30313d',
  colorText: 'white',
  colorDanger: '#df1b41',
  borderRadius: '4px',
  colorPrimaryText: 'white',
  fontSizeSm: '1rem',
};

// Elements gets no `clientSecret`: neither the card field nor the wallet button needs one to render, and Stripe won't
// accept one added after mount. A card checkout passes the SetupIntent's secret to `confirmCardSetup` instead.
export const elementsOptions: StripeElementsOptions = {appearance: {variables: stripeStyle}};

export type EmailConfirmationCheckoutScreenProps = {
  initialOption: ProductCode;
  otherOption: ProductCode;
  isStudent: boolean;
};

// Identifies this paywall on the shared low-cardinality `[Web Payments] Paywall *` events
// (Amplitude event-name cardinality stays flat; the paywall is a property, not a new event).
const PAYWALL: PaywallSlug = 'email-confirmation-checkout';

export const allowedLocale = (locale: string): SupportedCountryCode => {
  locale = locale.toUpperCase();

  if (['US', 'GB', 'AU', 'CA', 'DE'].includes(locale)) {
    return locale as SupportedCountryCode;
  }
  const otherEuroCountries = [
    'AT',
    'BE',
    'CY',
    'EE',
    'FI',
    'FR',
    'GR',
    'IE',
    'IT',
    'LV',
    'LT',
    'LU',
    'MT',
    'NL',
    'PT',
    'SK',
    'SI',
    'ES',
  ];
  if (otherEuroCountries.includes(locale)) {
    // FR and DE share EUR formatting and prices
    return 'DE';
  }
  return 'US';
};

const EmailConfirmationCheckoutScreen = (props: EmailConfirmationCheckoutScreenProps) => {
  useTranslation(); // will ensure the correct translation is used in `getEmailConfirmationProducts`
  const {initialOption, otherOption, isStudent} = props;
  useDocumentTitle('Rise');
  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);
  const token = useQueryToken();
  const localeParam: string = useValueParams('locale') ?? 'US';
  const locale = allowedLocale(localeParam);
  const history = useHistory();
  const [trialOption, setTrialOption] = useState<ProductCode>(initialOption);

  // Get reminderDaysBefore parameter from URL
  const reminderDaysBeforeParam = useValueParams('reminderDaysBefore');
  const reminderDaysBefore = reminderDaysBeforeParam ? parseInt(reminderDaysBeforeParam, 10) : undefined;

  const emailConfirmationProducts = getEmailConfirmationProducts();
  const product = emailConfirmationProducts[trialOption][locale];

  // I21 — second-chance offer (14-day free → annual). Passed to PaymentIntentScreen,
  // which surfaces it when the dismiss treatment opens it via the opener ref.
  const secondChanceOffer = getSecondChanceOffer(locale, isStudent);

  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | undefined>(undefined);
  const [paymentRequestLoading, setPaymentRequestLoading] = useState<boolean>(true);
  const [paymentTypes, setPaymentTypes] = useState<PaymentType[]>([]);
  const paymentMethodShownSent = useRef(false);
  // ms since page load when Paywall Viewed fired, so Payment Method Shown can report the wait after render.
  const paywallViewedAt = useRef<number | null>(null);
  const [clientSecret, setClientSecret] = useState<string>('');
  // stripe-setup-checkout's client secret as a promise, so a card checkout started before it answers can wait for it.
  const clientSecretRef = useRef<Promise<string | null>>(Promise.resolve(null));
  // A link-out visit already knows the person, so the payment UI doesn't wait for stripe-setup-checkout; that call
  // still answers "already subscribed" and makes the SetupIntent. Any other visit waits for it, as before.
  const redeemedPersonId = useRedeemedPersonId(token);
  const checkoutReady = Boolean(redeemedPersonId) || clientSecret !== '';
  // When checkout first became ready, stamped during render (an effect would add its own delay); see buttonWaitTiming.
  const checkoutReadyAt = useRef<number | null>(null);
  if (checkoutReady && checkoutReadyAt.current === null) {
    checkoutReadyAt.current = nowMs();
  }
  // Once shown, the payment UI stays mounted (hidden) if stripe-setup-checkout then says "already subscribed": a
  // wallet sheet opened in the meantime still needs PaymentIntentScreen's handler to finish, which treats Hypnos's
  // "User already subscribed" as an existing account. When that answer comes first, the UI never mounts.
  const paymentUiShown = useRef(false);
  const [duplicateCustomer, setDuplicateCustomer] = useState<boolean>(false);
  const [chargeDate, setChargeDate] = useState<string>('');
  const [prepaidBlockMode, setPrepaidBlockMode] = useState<'disallow_try_again' | 'disallow_and_redirect' | null>(null);

  // I21 — explicit dismiss treatment (immediate-x / delayed-x / idle-time), configured by
  // the `web_payments_dismiss_treatment` GrowthBook feature (with the `?dismissTreatment=`
  // URL param as a QA override). variant 'off' → fully inert: the ✕ never renders, no
  // timers arm, and the opener is never called, so default behavior is unchanged. The
  // opener ref is handed to PaymentIntentScreen, which owns the sheet.
  //
  // Runs once the payment UI is ready, with or without a wallet: the offer sheet takes
  // Apple/Google Pay or a card.
  const {variant: dismissVariant, revealMs, idleMs, configReady} = useDismissTreatmentConfig();
  const openSecondChanceRef = useRef<((source?: string) => void) | undefined>(undefined);
  const paymentReady = Boolean(stripeInstance) && checkoutReady && !duplicateCustomer && !paymentRequestLoading;
  const {showCloseButton, closeButtonVisible, triggerDismiss, onCheckoutStarted} = useDismissTreatment({
    variant: dismissVariant,
    ready: paymentReady,
    paywall: PAYWALL,
    configReady,
    revealMs,
    idleMs,
    openOffer: source => openSecondChanceRef.current?.(source),
  });

  // Paywall dismiss / dwell + refocus instrumentation (B08 / I21). Generic hook so other
  // paywalls can reuse it — see `usePaywallDismissTracking`. `markPurchased` is called on
  // success below so the post-purchase navigation isn't counted as a dismiss.
  const {markPurchased} = usePaywallDismissTracking({
    paywall: PAYWALL,
    properties: {
      email: token,
      locale,
      isStudent,
      trialOption,
      code: product.code,
      duplicateCustomer,
      paymentFormReady: Boolean(stripeInstance) && checkoutReady && !duplicateCustomer,
    },
  });

  // Standard paywall funnel events (Viewed / Trial Selection / Purchase Success). Legacy
  // per-screen names are dual-fired during the dashboard-migration window.
  const paywallAnalytics = usePaywallAnalytics({
    paywall: PAYWALL,
    baseProps: {email: token, locale, isStudent},
    legacy: {
      viewed: 'Email Confirmation Checkout Viewed',
      trialSelection: 'Email Confirmation Trial Selection',
      purchaseSuccess: 'Email Confirmation Purchase Success',
    },
  });

  useEffect(() => {
    if (paywallViewedAt.current === null) {
      paywallViewedAt.current = nowMs();
    }
    paywallAnalytics.viewed();
  }, [token, locale, isStudent, paywallAnalytics]);

  // Stripe and the wallet check (the payment-request effect below) don't need anything from
  // Hypnos, so they start alongside `stripe-setup-checkout` instead of after it. Waiting for
  // Hypnos first added about half a second to the median time before the button appeared.
  useEffect(() => {
    if (token) {
      loadWalletStripe().then(stripe => {
        if (stripe) {
          setStripeInstance(stripe);
        }
      });
    }
  }, [token]);

  // Setup Client Secret
  useEffect(() => {
    const setupCheckout = async (): Promise<string | null> => {
      const checkoutDetails = await createCheckoutDetails(token as string);
      if ('error' in checkoutDetails) {
        if (checkoutDetails.duplicateCustomer) {
          setDuplicateCustomer(true);
        } else {
          Analytics.track('Unable to set up Stripe Custom Checkout Page', {
            env: Environment.getVar('REACT_APP_ENV') ?? 'test',
            email: token,
          });
        }
        return null;
      }
      setClientSecret(checkoutDetails.client_secret);
      setChargeDate(checkoutDetails.charge_date);
      return checkoutDetails.client_secret;
    };
    if (token) {
      clientSecretRef.current = setupCheckout();
    }
  }, [token]);

  // Fire analytics for user who has a duplicate customer
  useEffect(() => {
    if (duplicateCustomer) {
      reportRepeatCheckout(product.code, token);
    }
  }, [duplicateCustomer, product.code, token]);

  // Create Payment Request - this will be recreated when the user changes the trial option
  useEffect(() => {
    if (stripeInstance) {
      if (paymentRequest) {
        paymentRequest.update({
          total: {
            label: product.sheetLabel,
            amount: product.price,
          },
        });
      } else {
        checkWallet(stripeInstance, product.price, product.sheetLabel, locale).then(
          ({paymentRequest, paymentTypes}) => {
            setPaymentRequest(paymentRequest);
            setPaymentTypes(paymentTypes);
            setPaymentRequestLoading(false);
          },
        );
      }
    } else {
      console.log('Unable to setup payment request, stripeInstance is null');
    }
  }, [paymentRequest, product.code, product.price, product.sheetLabel, stripeInstance, trialOption, locale]);

  // Report the method when the button can actually render: the wallet check is done and the person
  // is known (redeemed, or stripe-setup-checkout answered), so Payment Method Shown's `dwellMs` is
  // when the button appeared, after identify, and never for a known duplicate customer.
  useEffect(() => {
    // paymentReady (not just checkoutReady): React 17 doesn't batch the promise-callback state updates, so wait for
    // the commit where PaymentIntentScreen actually renders the button.
    if (paymentMethodShownSent.current || paymentTypes.length === 0 || !paymentReady) {
      return;
    }
    paymentMethodShownSent.current = true;
    const timing = buttonWaitTiming(paywallViewedAt.current, getWalletTiming(), checkoutReadyAt.current);
    paymentTypes.forEach(paymentType =>
      trackPaymentMethodShown(paymentType, {code: product.code, paywall: PAYWALL, timing}),
    );
  }, [paymentTypes, paymentReady, product.code]);

  const handleTrialSelection = (trialOption: ProductCode) => {
    paywallAnalytics.trialSelection({trialOption});
    setTrialOption(trialOption);
  };

  const handlePurchaseSuccess = ({existingAccount}: {existingAccount: boolean}) => {
    // Mark purchased so the page-leave handler doesn't count the post-purchase
    // navigation as a dismiss.
    markPurchased();
    paywallAnalytics.purchaseSuccess({trialOption});
    const reminderParam = reminderDaysBefore ? `&reminderDaysBefore=${reminderDaysBefore}` : '';
    history.push(`/email-confirmation-trial?${tokenParam(token)}days=${product.daysFree}${reminderParam}`);
  };
  // I21 — restore the native pay sheet to the currently-selected trial option after a
  // second-chance offer re-show is dismissed without purchase. The offer accept mutates
  // the shared paymentRequest to the offer's terms; this puts the selected option's
  // label/amount back so a subsequent normal checkout shows correct pricing.
  const handleSecondChanceDismissed = () => {
    if (paymentRequest) {
      paymentRequest.update({
        total: {
          label: product.sheetLabel,
          amount: product.price,
        },
      });
    }
  };
  const handleSecondChanceSuccess = () => {
    // Mark purchased so the page-leave handler doesn't count the post-purchase
    // navigation as a dismiss.
    markPurchased();
    // Offer purchases also fire the standard Paywall Purchase Success (secondChance: true) so
    // purchase funnels count both arms; PaymentIntentScreen adds Dismiss Offer Purchase Success.
    paywallAnalytics.purchaseSuccess({trialOption, secondChance: true, offerCode: secondChanceOffer.code});
    const reminderParam = reminderDaysBefore ? `&reminderDaysBefore=${reminderDaysBefore}` : '';
    history.push(`/email-confirmation-trial?${tokenParam(token)}days=${secondChanceOffer.daysFree}${reminderParam}`);
  };

  const refToScroll = useRef<HTMLDivElement>(null);

  const onCreditCardChoice = () => {
    refToScroll.current?.scrollIntoView({
      behavior: 'smooth',
    });
  };

  const handlePrepaidDetected = (mode: 'disallow_try_again' | 'disallow_and_redirect') => {
    setPrepaidBlockMode(mode);
    Analytics.track('Card Not Supported Sheet Viewed', {
      code: product.code,
      env: Environment.getVar('REACT_APP_ENV') ?? 'test',
      email: token,
      mode,
    });
  };

  useEffect(() => {
    const tokenIsLikelyAnId = token && !validateEmail(token);
    if (token && tokenIsLikelyAnId) {
      try {
        Analytics.identify(Number(token));
        Analytics.track('Identified user via token', {token: Number(token)});
      } catch (error) {
        Analytics.track('Unable to identify user via token', {token});
        Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
      }
    }
  }, [token]);

  return (
    <>
      <Helmet>
        {/* Prevents zooming into the text input when entering a credit card number */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
      </Helmet>
      <div className={`w-full h-screen mx-auto checkout-screen-background`}>
        {/* I21 dismiss affordance — the corner ✕ (immediate-x / delayed-x treatments). */}
        {showCloseButton ? <DismissCloseButton visible={closeButtonVisible} onClick={triggerDismiss} /> : null}
        <video
          autoPlay
          muted
          playsInline
          loop={true}
          id="Rise"
          className="fixed top-0 left-0 w-full object-cover object-top"
          style={{marginTop: '-5vh'}}
        >
          <source
            src="https://nyx-content-6xgk79jynn.s3.amazonaws.com/onboarding/paywall-v1b-edit-1290x2796-compressed-fast.mp4"
            type="video/mp4"
          />
          Your browser does not support HTML5 video.
        </video>

        <div
          ref={refToScroll}
          style={{
            position: 'absolute',
            left: 0,
            top: '25vh',
            backgroundColor: 'transparent',
            height: '1px',
            width: '100%',
          }}
        />
        <div className={`checkout-screen-content`}>
          <div style={{minHeight: window.innerHeight}} className={`h-screen w-full`}>
            <div className="pt-2 flex flex-col">
              <TrialSelectionCells
                onSelect={handleTrialSelection}
                selection={trialOption}
                products={[
                  getEmailConfirmationProducts()[initialOption][locale],
                  getEmailConfirmationProducts()[otherOption][locale],
                ]}
                isStudent={isStudent}
              />
              <div style={{paddingTop: '12px'}}>
                <TrialDetailsList trialOption={getEmailConfirmationProducts()[trialOption][locale]} />
              </div>
              <div>
                {duplicateCustomer && <div className={'text-white'}>Your subscription is active!</div>}
                {checkoutReady && stripeInstance && (!duplicateCustomer || paymentUiShown.current) ? (
                  <div
                    ref={el => {
                      if (el) paymentUiShown.current = true;
                    }}
                    className={growthBook.getFeatureValue('paywall_link_out_custom_cta', false) ? '' : 'pl-5 pr-5'}
                    style={{
                      ...(growthBook.getFeatureValue('paywall_link_out_custom_cta', false)
                        ? {marginLeft: '12px', marginRight: '12px'}
                        : {}),
                      ...(duplicateCustomer ? {display: 'none'} : {}),
                    }}
                  >
                    <Elements stripe={stripeInstance} options={elementsOptions}>
                      <PaymentIntentScreen
                        code={product.code}
                        paywall={PAYWALL}
                        paymentRequest={paymentRequest}
                        clientSecret={clientSecret || undefined}
                        getClientSecret={() => clientSecretRef.current}
                        daysFree={product.daysFree}
                        chargeDate={chargeDate}
                        dynamicCheckout={true}
                        onPurchaseSuccess={handlePurchaseSuccess}
                        secondChanceOffer={secondChanceOffer}
                        onSecondChanceSuccess={handleSecondChanceSuccess}
                        onSecondChanceDismissed={handleSecondChanceDismissed}
                        secondChanceOpenerRef={openSecondChanceRef}
                        onCheckoutStarted={onCheckoutStarted}
                        onPrepaidDetected={handlePrepaidDetected}
                        paymentRequestLoading={paymentRequestLoading}
                        onCreditCardChoice={onCreditCardChoice}
                        ctaText={product.daysFree === 7 ? `REDEEM 7 DAYS FREE` : `REDEEM ${product.daysFree} DAYS`}
                        useUpdatedCreditCardLogic={true}
                        useCustomCTA={true}
                        customCTAText={product.daysFree === 7 ? 'TRY FOR $0.00' : 'TRY FOR A MONTH'}
                      />
                    </Elements>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
        <BottomSheet
          isOpen={prepaidBlockMode !== null}
          onClose={() => {
            Analytics.track('Card Not Supported Sheet Dismissed', {
              code: product.code,
              env: Environment.getVar('REACT_APP_ENV') ?? 'test',
              email: token,
              mode: prepaidBlockMode,
            });
            setPrepaidBlockMode(null);
          }}
        >
          <div className="flex flex-col items-center text-center px-2 pb-4">
            {/* Card icon */}
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 16,
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect x="2" y="5" width="20" height="14" rx="2" stroke="#999" strokeWidth="2" />
                <line x1="2" y1="10" x2="22" y2="10" stroke="#999" strokeWidth="2" />
              </svg>
            </div>

            <h3 className="text-white text-xl font-bold mb-2">This card isn't supported</h3>

            {prepaidBlockMode === 'disallow_try_again' ? (
              <>
                <p className="text-gray-400 text-sm mb-6" style={{maxWidth: 280}}>
                  We weren't able to process this card. Please try a different card from your wallet.
                </p>
                <button
                  className="btn max-w-full w-full"
                  style={{height: 52, borderRadius: 12, fontSize: 16, fontWeight: 600}}
                  onClick={() => {
                    Analytics.track('Prepaid Try Different Card', {
                      code: product.code,
                      env: Environment.getVar('REACT_APP_ENV') ?? 'test',
                      email: token,
                    });
                    setPrepaidBlockMode(null);
                  }}
                >
                  Try a Different Card
                </button>
              </>
            ) : (
              <>
                <p className="text-gray-400 text-sm mb-6" style={{maxWidth: 280}}>
                  We weren't able to process this card. Finish setup in the app.
                </p>
                <a
                  href="nyx://"
                  onClick={() =>
                    Analytics.track('Prepaid Routed to App', {
                      code: product.code,
                      env: Environment.getVar('REACT_APP_ENV') ?? 'test',
                      email: token,
                    })
                  }
                  className="btn max-w-full w-full flex items-center justify-center gap-2"
                  style={{
                    height: 52,
                    borderRadius: 12,
                    fontSize: 16,
                    fontWeight: 600,
                    textDecoration: 'none',
                  }}
                >
                  Continue in the App
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{marginLeft: 4}}>
                    <path
                      d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </a>
              </>
            )}
          </div>
        </BottomSheet>

        <MaybeStagingBadge />
      </div>
    </>
  );
};

export default EmailConfirmationCheckoutScreen;
